import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Container, Clock, MessageSquare, Send, X, Trash2, AlertTriangle, ChevronDown, ChevronUp, Upload, Loader2, Wrench, FileText, Download } from "lucide-react";
import { format } from "date-fns";
import { DwellTimeChip } from "@/components/yard/DwellTimeChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BolDocumentPreview } from "@/components/orders/BolViewerDialog";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { useTrailerImageUrl } from "@/lib/trailer-image";
import { useTrailerEventPhotos, type TrailerEventPhoto } from "@/lib/trailer-event-photos";
import { TrailerPhotoGallery } from "@/components/yard-view/TrailerPhotoGallery";
import { uploadBolPhoto } from "@/components/yard/yardPhotoUtils";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";
import {
  TrailerCrossTenantDialog,
  findTrailerInOtherTenants,
} from "@/components/trailers/TrailerCrossTenantDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const EVENT_VERB: Record<string, string> = {
  checked_in: "Checked into",
  checked_out: "Checked out of",
  audited: "Audited at",
};

interface TrailerDetailDialogProps {
  trailerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Show the Notes/Comments section. Default true. */
  showComments?: boolean;
  /** Show the destructive delete action (only fires when current_location_status === "unknown"). */
  showDelete?: boolean;
  onDelete?: (id: string) => void;
  /** Show the cross-tenant existence warning. */
  showCrossTenantWarning?: boolean;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2 px-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

interface ActivityCardProps {
  event: {
    id: string;
    event_type: string;
    occurred_at: string;
    truck_id: string | null;
    seal_id: string | null;
    load_status: string | null;
    seal_status: string | null;
    location: { short_code: string } | null;
  };
  docs: Array<{ id: string; file_path: string; file_name: string }>;
  trailerScac: string | null;
  trailerTypeLabel: string | null;
  authorLabel: string | null;
  onViewDoc: (filePath: string, fileName: string) => void;
}

function ActivityCard({ event, docs, trailerScac, trailerTypeLabel, authorLabel, onViewDoc }: ActivityCardProps) {
  const [expanded, setExpanded] = useState(false);
  const verb = EVENT_VERB[event.event_type] ?? event.event_type;
  const site = event.location?.short_code ?? "Unknown site";
  const when = format(new Date(event.occurred_at), "MMM d 'at' h:mm a zzz");
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-muted/40 transition-colors"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{verb} {site}</p>
          <p className="text-xs text-muted-foreground">
            {when}{authorLabel ? ` by ${authorLabel}` : ""}
          </p>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          <div className="divide-y divide-border border-t">
            <DetailRow label="Truck ID" value={event.truck_id} />
            <DetailRow label="Trailer type" value={trailerTypeLabel ?? "Unknown"} />
            <DetailRow label="SCAC" value={trailerScac} />
            <DetailRow label="Load status" value={event.load_status} />
            <DetailRow label="Seal status" value={event.seal_status} />
            <div className="flex justify-between items-start py-2 px-2 gap-2">
              <span className="text-sm text-muted-foreground shrink-0">BOL</span>
              {docs.length === 0 ? (
                <span className="text-sm font-medium text-right">—</span>
              ) : (
                <div className="flex flex-col items-end gap-1 min-w-0">
                  {docs.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => onViewDoc(d.file_path, d.file_name)}
                      className="text-sm font-medium text-primary hover:underline truncate max-w-[220px]"
                    >
                      {d.file_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TrailerDetailDialog({
  trailerId,
  open,
  onOpenChange,
  showComments = true,
  showDelete = false,
  onDelete,
  showCrossTenantWarning = false,
}: TrailerDetailDialogProps) {
  const { user } = useAuth();
  const { tenantId, activeTenant } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState("");
  const [showCrossTenantDialog, setShowCrossTenantDialog] = useState(false);
  const { data: trailerTypes } = useTrailerTypes();

  const { data: trailer } = useQuery({
    queryKey: ["yard-view-trailer-detail", trailerId],
    enabled: !!trailerId && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select(`
          *,
          current_location:locations!trailers_current_location_id_fkey(short_code)
        `)
        .eq("id", trailerId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: events } = useQuery({
    queryKey: ["yard-view-trailer-events", trailerId],
    enabled: !!trailerId && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_events")
        .select(`*, location:locations!trailer_events_location_id_fkey(short_code)`)
        .eq("trailer_id", trailerId!)
        .order("occurred_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // User directory for resolving event author display names.
  const { data: userDirectory } = useQuery({
    queryKey: ["tenant-user-directory", tenantId],
    enabled: !!tenantId && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_mentionable_users_for_tenant", {
        _tenant_id: tenantId!,
      });
      if (error) throw error;
      const map = new Map<string, { email: string | null; full_name: string | null }>();
      for (const row of data ?? []) {
        map.set(row.user_id, { email: row.email, full_name: row.full_name });
      }
      return map;
    },
  });

  // Cross-tenant matches (Trailers page only).
  const { data: otherTenantMatches = [] } = useQuery({
    queryKey: ["trailer-cross-tenant-matches", trailer?.trailer_id, tenantId],
    enabled: showCrossTenantWarning && !!trailer?.trailer_id && !!tenantId && open,
    queryFn: () => findTrailerInOtherTenants(trailer!.trailer_id, tenantId!),
    staleTime: 30_000,
  });


  const { data: comments } = useQuery({
    queryKey: ["yard-view-trailer-comments", trailerId],
    enabled: !!trailerId && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_comments")
        .select("*")
        .eq("trailer_id", trailerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch BOL documents from trailer events
  const { data: bolDocs } = useQuery({
    queryKey: ["yard-view-trailer-bols", trailerId],
    enabled: !!trailerId && open,
    queryFn: async () => {
      const { data: tevents, error: evtErr } = await supabase
        .from("trailer_events")
        .select("id, occurred_at, location:locations!trailer_events_location_id_fkey(short_code)")
        .eq("trailer_id", trailerId!)
        .order("occurred_at", { ascending: false });
      if (evtErr) throw evtErr;
      if (!tevents.length) return [];

      const eventIds = tevents.map((e) => e.id);
      const { data: documents, error: docErr } = await supabase
        .from("trailer_event_documents")
        .select("id, file_path, file_name, document_type, created_at, trailer_event_id")
        .in("trailer_event_id", eventIds)
        .order("created_at", { ascending: false });
      if (docErr) throw docErr;

      const eventMap = new Map(tevents.map((e) => [e.id, e]));
      return documents.map((doc) => {
        const evt = eventMap.get(doc.trailer_event_id);
        return {
          id: doc.id,
          file_path: doc.file_path,
          file_name: doc.file_name,
          document_type: doc.document_type,
          created_at: doc.created_at,
          trailer_event_id: doc.trailer_event_id,
          event: evt ? { occurred_at: evt.occurred_at, location: evt.location as { short_code: string } | null } : null,
        };
      });
    },
  });

  // Find orders assigned to this trailer
  const { data: assignedOrders } = useQuery({
    queryKey: ["yard-view-trailer-orders", trailer?.trailer_id],
    enabled: !!trailer?.trailer_id && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, unique_id, status")
        .eq("trailer_id", trailer!.trailer_id)
        .not("status", "eq", "canceled")
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async (comment: string) => {
      const { error } = await supabase.from("trailer_comments").insert({
        trailer_id: trailerId!,
        comment,
        created_by_user_id: user!.id,
        created_by_email: user!.email,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yard-view-trailer-comments", trailerId] });
      setNewComment("");
      toast.success("Comment added");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmitComment = () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;
    addCommentMutation.mutate(trimmed);
  };

  const [previewDoc, setPreviewDoc] = useState<{ filePath: string; fileName: string } | null>(null);
  const handleViewDoc = (filePath: string, fileName?: string) => {
    setPreviewDoc({ filePath, fileName: fileName ?? filePath.split("/").pop() ?? "Document" });
  };

  const bolFileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadBolMutation = useMutation({
    mutationFn: async (file: File) => {
      const latestEventId = events?.[0]?.id;
      if (!latestEventId) throw new Error("No check-in event to attach BOL to");
      await uploadBolPhoto(latestEventId, file, user?.id, user?.email ?? undefined, tenantId ?? undefined);
      // Push the freshly-attached BoL out to any orders already assigned to
      // this trailer (reverse-direction sync). Fresh upload → no carry-forward.
      await syncTrailerBol({
        direction: "trailer-gains-bol",
        trailerHumanId: trailer?.trailer_id,
        tenantId,
        tenantSlug: activeTenant,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yard-view-trailer-bols", trailerId] });
      queryClient.invalidateQueries({ queryKey: ["yard-view-trailer-events", trailerId] });
      toast.success("BOL uploaded");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Signed URL for the private `trailer-images` bucket. Hook handles
  // null `image_path` by returning null. Kept as a fallback for trailers
  // that pre-date the typed `trailer_event_photos` gallery.
  const imageUrl = useTrailerImageUrl(trailer?.image_path);
  const eventPhotos = useTrailerEventPhotos(trailer?.id);
  const galleryPhotos: TrailerEventPhoto[] = eventPhotos.length
    ? eventPhotos
    : trailer?.image_path && imageUrl
    ? [{
        id: "legacy",
        photo_type: "trailer",
        image_path: trailer.image_path,
        created_at: "",
        signedUrl: imageUrl,
      }]
    : [];

  if (!trailer) return null;

  const locationStatus = trailer.current_location_status === "at_site"
    ? "At Site"
    : trailer.current_location_status === "in_transit"
    ? "In Transit"
    : "Unknown";

  const formatEventAuthor = (userId: string | null | undefined): string | null => {
    if (!userId) return null;
    const u = userDirectory?.get(userId);
    if (!u) return "Unknown user";
    return u.full_name || u.email || "Unknown user";
  };

  // Group BOL docs by event id for inline rendering.
  const docsByEvent = new Map<string, NonNullable<typeof bolDocs>>();
  for (const d of bolDocs ?? []) {
    const arr = docsByEvent.get(d.trailer_event_id) ?? [];
    arr.push(d);
    docsByEvent.set(d.trailer_event_id, arr as NonNullable<typeof bolDocs>);
  }

  const latestEvent = events?.[0] ?? null;
  const latestEventDocs = latestEvent ? docsByEvent.get(latestEvent.id) ?? [] : [];
  const latestBol = latestEventDocs[0] ?? null;

  const eventLoadStatus = (latestEvent as any)?.load_status as string | null | undefined;
  const eventSealId = (latestEvent as any)?.seal_id as string | null | undefined;
  const eventSealStatus = (latestEvent as any)?.seal_status as string | null | undefined;
  const eventTruckId = (latestEvent as any)?.truck_id as string | null | undefined;
  const eventLocation = (latestEvent as any)?.location?.short_code as string | undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="right" className="w-full lg:w-[440px] lg:max-w-[440px] max-w-full overflow-y-auto [&>button.absolute]:hidden p-0">
        {/* Action bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
          {showDelete && onDelete && trailer.current_location_status === "unknown" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete trailer {trailer.trailer_id}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this trailer and all its event history and comments.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(trailer.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Header */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2">
            <Container className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold font-mono">{trailer.trailer_id}</h2>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Badge variant={trailer.current_location_status === "at_site" ? "default" : "secondary"}>
              {locationStatus}
            </Badge>
            {trailer.current_location_status === "at_site" && events?.[0]?.event_type === "checked_in" && (
              <DwellTimeChip occurredAt={events[0].occurred_at} />
            )}
            {trailer.scac && (
              <Badge variant="outline" className="text-xs">{trailer.scac}</Badge>
            )}
            {trailer.needs_service && (
              <Badge variant="destructive" className="gap-1 text-xs">
                <Wrench className="h-3 w-3" />
                Needs Service
              </Badge>
            )}
          </div>
        </div>

        {/* Sections */}
        <div className="px-4 pb-4 space-y-4">
          {/* Needs Service callout */}
          {trailer.needs_service && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <div className="flex items-start gap-2">
                <Wrench className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">Needs Service</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This trailer is blocked from order assignment until cleared on a later check-in.
                  </p>
                  <div className="mt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Reported issue
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap mt-0.5">
                      {trailer.needs_service_reason?.trim() || "No reason provided"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Cross-tenant warning */}
          {showCrossTenantWarning && otherTenantMatches.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCrossTenantDialog(true)}
              className="w-full rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-left transition-colors hover:bg-amber-500/15"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500 shrink-0" />
                <div className="text-xs">
                  <p className="font-medium text-foreground">
                    Exists in {otherTenantMatches.length === 1 ? "another tenant" : `${otherTenantMatches.length} other tenants`}
                  </p>
                  <p className="text-muted-foreground mt-0.5">
                    {otherTenantMatches.map((m) => m.tenant_slug).join(", ")} · click to review or migrate
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Photo gallery — trailer, seal, license plate. Falls back to
              the legacy `trailers.image_path` single image for records
              captured before typed photos were introduced. */}
          {galleryPhotos.length > 0 && (
            <TrailerPhotoGallery photos={galleryPhotos} />
          )}

          {/* Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Container className="h-4 w-4" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                <DetailRow label="Trailer type" value={getTrailerTypeLabel(trailer.trailer_type_id, trailerTypes) ?? "Unknown"} />
                <DetailRow label="Location" value={eventLocation ?? (trailer as any)?.current_location?.short_code ?? "Unknown"} />
                {(eventTruckId ?? null) !== null && <DetailRow label="Truck ID" value={eventTruckId} />}
                {trailer.scac && <DetailRow label="SCAC" value={trailer.scac} />}
                <DetailRow label="Load Status" value={eventLoadStatus ?? trailer.load_status ?? "Unknown"} />
                {(eventSealId ?? trailer.seal_id) && <DetailRow label="Seal" value={eventSealId ?? trailer.seal_id} />}
                <DetailRow label="Seal Status" value={eventSealStatus ?? (trailer as any).seal_status ?? "Unknown"} />
                <div className="flex justify-between items-start py-2 px-2 gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">BOL</span>
                  {latestBol ? (
                    <button
                      type="button"
                      onClick={() => handleViewDoc(latestBol.file_path, latestBol.file_name)}
                      className="text-sm font-medium text-primary hover:underline truncate text-right max-w-[60%]"
                    >
                      {latestBol.file_name}
                    </button>
                  ) : (
                    <>
                      <input
                        ref={bolFileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadBolMutation.mutate(file);
                          if (bolFileInputRef.current) bolFileInputRef.current.value = "";
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => bolFileInputRef.current?.click()}
                        disabled={uploadBolMutation.isPending || !events?.[0]?.id}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        {uploadBolMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Upload className="h-3.5 w-3.5" />
                        )}
                        Upload
                      </button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Uploaded BOLs */}
          {bolDocs && bolDocs.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Uploaded BOLs ({bolDocs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {bolDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-2 rounded-md border p-2.5"
                  >
                    <button
                      type="button"
                      onClick={() => handleViewDoc(doc.file_path, doc.file_name)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <p className="text-sm font-medium text-primary hover:underline truncate">
                        {doc.file_name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Uploaded {format(new Date(doc.created_at), "MMM d, yyyy · h:mm a")}
                      </p>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      title="Download"
                      onClick={async () => {
                        const { data, error } = await supabase.storage
                          .from("idp-uploads")
                          .createSignedUrl(doc.file_path, 60, { download: doc.file_name });
                        if (error || !data?.signedUrl) {
                          toast.error(error?.message ?? "Could not generate download link");
                          return;
                        }
                        window.open(data.signedUrl, "_blank", "noopener");
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Associated Orders */}
          {assignedOrders && assignedOrders.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Associated Orders ({assignedOrders.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {assignedOrders.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => { onOpenChange(false); navigate(`/orders/${o.id}`); }}
                    className="block text-sm font-medium text-primary hover:underline"
                  >
                    {o.unique_id}
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Activity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!events?.length ? (
                <p className="text-sm text-muted-foreground">No events recorded</p>
              ) : (
                <div className="rounded-md border divide-y overflow-hidden">
                  {events.map((event) => (
                    <div key={event.id}>
                      <ActivityCard
                        event={event as any}
                        docs={docsByEvent.get(event.id) ?? []}
                        trailerScac={trailer.scac ?? null}
                        trailerTypeLabel={getTrailerTypeLabel(trailer.trailer_type_id, trailerTypes)}
                        authorLabel={formatEventAuthor((event as any).created_by_user_id)}
                        onViewDoc={handleViewDoc}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          {showComments && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Add comment */}
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a comment…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[60px] text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      handleSubmitComment();
                    }
                  }}
                />
                <Button
                  size="icon"
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || addCommentMutation.isPending}
                  className="flex-shrink-0 self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              {!comments?.length ? (
                <p className="text-sm text-muted-foreground">No comments yet</p>
              ) : (
                <div className="space-y-2">
                  {comments.map((comment) => (
                    <div key={comment.id} className="rounded-md border p-2.5 text-sm space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-xs truncate">
                          {comment.created_by_email ?? "Unknown"}
                        </span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {format(new Date(comment.created_at), "MMM d, h:mm a")}
                        </span>
                      </div>
                      <p className="text-muted-foreground whitespace-pre-wrap">{comment.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
        {showCrossTenantWarning && otherTenantMatches.length > 0 && (
          <TrailerCrossTenantDialog
            open={showCrossTenantDialog}
            onOpenChange={setShowCrossTenantDialog}
            trailerIdText={trailer.trailer_id}
            matches={otherTenantMatches}
          />
        )}
        <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="truncate">{previewDoc?.fileName ?? "Document"}</DialogTitle>
            </DialogHeader>
            {previewDoc && (
              <BolDocumentPreview filePath={previewDoc.filePath} bucket="idp-uploads" />
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
